/* eslint-disable @typescript-eslint/no-explicit-any */
import rcclUUID from '@rcg/uuid';
import { Dictionary } from 'contexts/appContext';
import { Selection, TypedSelection } from './../components/FiltersProvider/types';
import defaultDictionary from './../resources/defaultDictionary';
import { getConfig } from './config';
import { BrandCode, Locale } from './config/config';
import { fetchData } from './fetcher';
import { GraphQLClient, GraphQLQueryFiltersData, GraphQLQueryLocaleData } from './graphqlClient';
import filtersFacetsQuery from './graphqlQueries/filterFacets';
import filtersGraphQLQuery from './graphqlQueries/filterOptions';
import localeGraphQLQuery from './graphqlQueries/locale';
import { filtersToGraph } from './utils/filters';

export const CUSTOM_FILTERS_KEY = 'custom';

/** GraphQLResults */

export enum GraphQLFilterTypes {
	BOOLEAN = 'CruiseBooleanFilter',
	LIST = 'CruiseListFilter',
	GROUP_LIST = 'CruiseGroupListFilter',
	NUMBER_RANGE = 'CruiseNumberRangeFilter',
	DATE_RANGE = 'CruiseDateRangeFilter',
	CUSTOM = 'CruiseCustomFilterOption',
	SORT = 'CruiseSortFilterOption',
}

export interface GraphQLUrlParam {
	key: string;
	value: string;
}

export interface GraphQLFacetOption {
	code: string;
	count: number;
}

export interface FilterFacet {
	key: string;
	facets: Array<GraphQLFacetOption>;
}

export interface GraphQLFilter {
	__typename: string;
	key: string;
	facets: Array<GraphQLFacetOption>;
}

export interface GraphQLListFilterOption {
	code: string;
	label: string;
	containers?: Array<string>;
	criteria?: Selection;
	urlParams?: Array<GraphQLUrlParam>;
	section?: Array<string>;
}

export interface GraphQLListFilter extends GraphQLFilter {
	options: Array<GraphQLListFilterOption>;
}

export interface GraphQLGroupFilterOption {
	code: string;
	label: string;
	options: Array<GraphQLListFilterOption>;
}

export interface GraphQLGroupFilter extends GraphQLFilter {
	groups: Array<GraphQLGroupFilterOption>;
}

export interface GraphQLNumberRangeFilter extends GraphQLFilter {
	min: number;
	max: number;
}

export interface GraphQLDateRangeFilter extends GraphQLFilter {
	startDate: string;
	endDate: string;
}

/** Widget Filter Data */
export interface Filter {
	key: string;
	type: string;
	label: string;
	isEnabled: boolean;
}

export interface BooleanOption extends Filter {
	isDisabled: boolean;
	isSelected: boolean;
}

export interface ListFilterOption {
	code: string;
	label: string;
	isDisabled: boolean;
	isSelected: boolean;
	containers?: Array<string>;
	criteria?: TypedSelection;
	urlParams?: Array<GraphQLUrlParam>;
	section?: Array<string>;
}
export interface ListFilter extends Filter {
	options: Array<ListFilterOption>;
}

export interface OcupancyFilter extends Filter {
	options: [
		{
			code: number;
			label: string;
		},
	];
}
export interface GroupListFilterOption {
	code: string;
	label: string;
	isDisabled: boolean;
	isSelected: boolean;
	options: Array<ListFilterOption>;
}
export interface GroupListFilter extends Filter {
	groups: Array<GroupListFilterOption>;
}

export interface DateRangeFilterMonth {
	code: string;
	label: string;
	isDisabled: boolean;
	isSelected: boolean;
	dateRange: string;
}

export interface DateRangeFilterYear {
	code: string;
	label: string;
	isDisabled: boolean;
	isSelected: boolean;
	months: Array<DateRangeFilterMonth>;
}
export interface DateRangeFilter extends Filter {
	key: string;
	label: string;
	startDate: Date;
	endDate: Date;
	years: Array<DateRangeFilterYear>;
}

const getLocaleHeaders = (locale: Locale): Pick<Locale, 'country' | 'currency' | 'language' | 'office' | 'countryAlpha2Code'> => {
	const { country, currency, language, office, countryAlpha2Code } = locale;

	return {
		country,
		currency,
		language,
		office,
		countryAlpha2Code,
	};
};

export function getSessionId(): string {
	if (typeof window !== 'undefined') {
		return rcclUUID.getRWDId();
	}

	return '';
}

// TODO: Add variables and headers
//       Returns Array<Filter>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getFilters(locale: Locale, brand: BrandCode): Promise<Array<any>> {
	const graphQLClient = new GraphQLClient();
	const response = await graphQLClient.query<GraphQLQueryFiltersData>(
		filtersGraphQLQuery,
		{},
		{ queryName: filtersGraphQLQuery.name, ...getLocaleHeaders(locale), brand, sessionId: getSessionId() },
	);
	return response?.cruiseSearch?.filters ?? [];
}

export async function getFiltersFacets(
	filtersSelection: Selection,
	abortController: AbortController,
	locale: Locale,
	brand: BrandCode,
): Promise<Array<FilterFacet>> {
	const graphQLClient = new GraphQLClient();
	const filters: string = filtersToGraph(filtersSelection);
	const response = await graphQLClient.query<GraphQLQueryFiltersData>(
		filtersFacetsQuery,
		{ filters },
		{ queryName: filtersFacetsQuery.name, ...getLocaleHeaders(locale), brand, sessionId: getSessionId() },
		abortController,
	);
	return response?.cruiseSearch?.filters ?? [];
}

export async function getDictionary(language: string): Promise<Dictionary> {
	const config = getConfig();
	let dictionary = defaultDictionary;

	let url;
	try {
		url = new URL(config.endpoints.aemDictionaries);
	} catch {
		url = new URL(config.endpoints.aemDictionaries, window.location.origin);
	}
	url.search = new URLSearchParams({
		dictName: 'cruise-search/widget',
		langCd: language,
	}).toString();

	const response = await fetchData(url.href);

	if (response?.data) {
		const newDictionary = response.data;
		dictionary = { ...defaultDictionary, ...newDictionary };
	}

	return dictionary;
}

export async function getLocale(country: string, language: string, brand: BrandCode): Promise<any> {
	const graphQLClient = new GraphQLClient();
	const response = await graphQLClient.query<GraphQLQueryLocaleData>(
		localeGraphQLQuery,
		{ country, language },
		{ queryName: localeGraphQLQuery.name, brand, sessionId: getSessionId() },
	);

	return response?.locale
		? {
				country: response?.locale.country.code,
				currency: response?.locale?.currency.code,
				office: response?.locale.country.office.code,
				language: response?.locale.language.code,
				path: response?.locale.path,
				countryAlpha2Code: response?.locale.country.isoAlpha2Code,
			}
		: {};
}
